import { memo, useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import type { Entity } from '../state/store';
import { useAppStore } from '../state/store';
import { dispatchEntityClick, useEntityRegistryRef } from './toolEventBus';
import { useAnnotationInk } from '../hooks/useThemeInk';
import { subdLimit, cageToTriangles, type SubdCage } from '../core/geometry/subd/subd';
import { BACKFACE_DEFINES } from './backfaceTint';

/**
 * SubD renderer (R3): the entity data is the control cage; this component
 * derives the display mesh. `subdSmooth` selects the limit (2 Catmull-Clark
 * passes) or the raw cage — the SUBDDISPLAY toggle's real consumer — and
 * `crease` (0..1) is honored by the CC step (1 keeps the cage sharp).
 * The cage wireframe is drawn when selected (3D-style cage display).
 */
export const SubdEntityMesh = memo(function SubdEntityMesh({
    entity, isSelected, isHovered,
}: { entity: Entity; isSelected: boolean; isHovered: boolean }) {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : (entity.data.color as string) || '#c084fc';
    // Cage wireframe strokes follow the theme ink: the pale lavender cage is
    // unreadable on the light viewport in wireframe display mode.
    const ink = useAnnotationInk();
    const cage = entity.data.cage as SubdCage | undefined;
    const viewMode = useAppStore((s) => s.view.viewMode);
    const wireframe = viewMode === 'wireframe' || viewMode === 'hiddenLine';
    const smooth = entity.data.subdSmooth !== false;
    const crease = Number(entity.data.crease) || 0;

    const geometry = useMemo(() => {
        if (!cage || cage.vertices.length === 0) return null;
        const display = smooth ? cageToTriangles(subdLimit(cage, 2, crease)) : cageToTriangles(cage);
        if (display.vertices.length === 0 || display.indices.length === 0) return null;
        const geo = new THREE.BufferGeometry();
        const pos = new Float32Array(display.vertices.length * 3);
        display.vertices.forEach((v, i) => { pos[i * 3] = v[0]; pos[i * 3 + 1] = v[1]; pos[i * 3 + 2] = v[2] ?? 0; });
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        const idx: number[] = [];
        display.indices.forEach((t) => { if (t.length === 3) idx.push(t[0], t[1], t[2]); });
        geo.setIndex(idx);
        geo.computeVertexNormals();
        return geo;
    }, [cage, smooth, crease]);

    const cageLines = useMemo(() => {
        // selected (3D cage display) or wireframe display mode (R6-2)
        if (!cage || !(isSelected || wireframe)) return [];
        const pts: [number, number, number][][] = [];
        cage.faces.forEach((f) => {
            const line: [number, number, number][] = f.map((i) => {
                const v = cage.vertices[i];
                return [v[0], v[1], v[2] ?? 0] as [number, number, number];
            });
            line.push(line[0]);
            pts.push(line);
        });
        return pts;
    }, [cage, isSelected, wireframe]);

    const refCallback = useEntityRegistryRef(entity.id, entity.layer);
    if (!geometry) return null;
    return (
        <group>
            <mesh
                ref={refCallback}
                geometry={geometry}
                onClick={(e) => { e.stopPropagation(); dispatchEntityClick(entity.id, e as any); }}
                castShadow
                receiveShadow
            >
                <meshStandardMaterial
                    color={color}
                    roughness={0.55}
                    metalness={0.1}
                    side={THREE.DoubleSide}
                    defines={BACKFACE_DEFINES}
                    transparent
                    opacity={isSelected ? 1 : viewMode === 'xray' ? 0.25 : viewMode === 'ghosted' ? 0.35 : 0.95}
                />
            </mesh>
            {cageLines.map((line, i) => (
                <Line key={i} points={line} color={ink} lineWidth={0.7} />
            ))}
        </group>
    );
});
